import React, { useState, useRef, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { AnimatePresence, motion } from "framer-motion";
import { observer, inject } from "mobx-react";
import CloseOneIcon from '../svg/CloseOneIcon';
import Trash from '../svg/Trash';
import SaveIcon from '../svg/SaveIcon';
import CloudDownloadIcon from '../svg/CloudDownloadIcon';
import ShareIcon from '../svg/ShareIcon';
import ClearCartModal from '../modals/ClearCartModal';
import SaveCartModal from '../modals/SaveCartModal';
import LoadCartModal from '../modals/LoadCartModal';
import ShareCartModal from '../modals/ShareCartModal';
import MoreOptionsIcon from '../svg/MoreOptionsIcon';
import BoxOneIcon from '../svg/BoxOneIcon';
import MiniCartTotals from './MiniCartTotals';
import MiniCartProduct from './MiniCartProduct';
import Link from '@/components/common/Link';

const MiniCartContent = inject('studioStore', 'generalSettingsStore', 'wooCommerceStore')(observer(({
  toggleCart,
  clearCart,
  productCount,
  cartItems,
  isOpen,
  studioStore,
  generalSettingsStore,
}) => {
  const tg = useTranslations('General');
  const t = useTranslations('Cart');
  const ta = useTranslations('Account');
  const [showClearCartModal, setShowClearCartModal] = useState(false);
  const [showSaveCartModal, setShowSaveCartModal] = useState(false);
  const [showLoadCartModal, setShowLoadCartModal] = useState(false);
  const [showShareCartModal, setShowShareCartModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const container = studioStore?.cart?.container;
  const cartBackgroundColor = studioStore?.cart?.background_color || 'white';
  const primaryColor = studioStore?.settings?.colors?.primary_color;

  // Check if cart management is enabled
  const isCartManagementEnabled = generalSettingsStore.showCartManageMultiple;

  // Check login status on client-side only
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  // Handle click outside to close menu
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  let classes = "mini-cart-content";
  const style = {
    backgroundColor: cartBackgroundColor,
  };

  if (container === "stamp") {
    classes = classes + " stamp-border";
    style.backgroundImage = `radial-gradient(8px, rgba(255, 255, 255, 0) 98%, ${cartBackgroundColor}),linear-gradient(${cartBackgroundColor} 0px, ${cartBackgroundColor} 0px)`;
    style.backgroundColor = '';
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      {isOpen && (
        <>
          <motion.div
            aria-hidden="true"
            onClick={toggleCart}
            className="fixed bottom-0 left-0 right-0 top-0 z-40 bg-[rgba(0,0,0,0.3)]"
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
              transition: {
                delay: 0.2,
              },
            }}
            transition={{
              duration: 0.3,
            }}
          ></motion.div>
          <motion.div
            className={`${classes} fixed left-0 top-0 h-full w-full md:w-1/2 xl:w-[35%] 2xl:w-[27%] z-50 flex flex-col`}
            style={style}
            initial={{
              opacity: 0,
              x: '-100%',
            }}
            animate={{
              x: 0,
              opacity: 1,
            }}
            exit={{
              x: '-100%',
            }}
            transition={{
              duration: 0.3,
            }}
          >
            <div className="px-0 py-2 md:p-2 text-primary-studio">
              <div className="flex flex-row-reverse z-50 relative">
                <div className="text-center h-8">
                  <button onClick={toggleCart} title={tg('close')} data-testid="cart-close-button">
                    <CloseOneIcon size="30px" stroke={primaryColor} />
                  </button>
                </div>
              </div>
              <div className="flex -mt-4 justify-between items-center relative z-10">
                <div>
                  <h4 className="text-3xl font-bold font-secondary">
                    {t('my_cart')}
                  </h4>
                  <div className="flex items-center">
                    <span className="font-bold font-secondary inline-block me-4">{t('total_of')} {productCount} {t('cart_items')}</span>
                    {cartItems.length > 0 &&
                      <button onClick={() => setShowClearCartModal(true)} title={t('clear_cart')} data-testid="clear-cart-button">
                        <Trash size="20px" stroke={primaryColor} className="inline-block" />
                        <span className="inline-block font-bold font-secondary">{t('clear')}</span>
                      </button>}
                  </div>
                </div>

                {isCartManagementEnabled && cartItems.length > 0 && isLoggedIn && (
                  <div className="cart-menu" ref={menuRef}>
                    <button
                      onClick={() => setIsMenuOpen(!isMenuOpen)}
                      className="flex items-center justify-center bg-primary-studio rounded-full p-1.5 bottom-0 right-0 rtl:left-0 rtl:right-auto absolute"
                    >
                      <MoreOptionsIcon stroke="white" size="20px" />
                    </button>

                    {isMenuOpen && (
                      <div className="absolute right-0 rtl:left-0 top-full rtl:right-auto mt-2 w-36 bg-white rounded-md shadow-lg py-1 z-50">
                        <button
                          onClick={() => {
                            setShowSaveCartModal(true);
                            setIsMenuOpen(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <SaveIcon size="18px" stroke={primaryColor} className="me-2" />
                          <span>{t('save_cart')}</span>
                        </button>
                        <button
                          onClick={() => {
                            setShowLoadCartModal(true);
                            setIsMenuOpen(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <CloudDownloadIcon size="18px" stroke={primaryColor} className="me-2" />
                          <span>{t('load_cart')}</span>
                        </button>
                        <button
                          onClick={() => {
                            setShowShareCartModal(true);
                            setIsMenuOpen(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <ShareIcon size="18px" stroke={primaryColor} className="me-2" />
                          <span>{t('share_cart')}</span>
                        </button>
                        <Link
                          href="/my-account?tab=manage-carts"
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => {
                            setIsMenuOpen(false);
                            toggleCart();
                          }}
                        >
                          <BoxOneIcon width="20px" height="20px" fill={primaryColor} className="me-2" />
                          <span>{ta('manage_carts')}</span>
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mini-cart-product-list flex-1 overflow-y-auto scrollbar px-0 md:px-2">
              {cartItems.length > 0 ? (
                cartItems.map(item => (
                  <MiniCartProduct key={item.id} item={item} />
                ))
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 text-lg" data-testid="empty-cart">
                  {t("empty_cart")}
                </div>
              )}
            </div>
            {cartItems.length > 0 && <MiniCartTotals />}

            <ClearCartModal
              isOpen={showClearCartModal}
              onClose={() => setShowClearCartModal(false)}
              onConfirm={() => {
                setShowClearCartModal(false);
                clearCart();
              }}
              studioStore={studioStore}
            />

            {isCartManagementEnabled && (
              <>
                <SaveCartModal
                  isOpen={showSaveCartModal}
                  onClose={() => setShowSaveCartModal(false)}
                />

                <LoadCartModal
                  isOpen={showLoadCartModal}
                  onClose={() => setShowLoadCartModal(false)}
                />

                <ShareCartModal
                  isOpen={showShareCartModal}
                  onClose={() => setShowShareCartModal(false)}
                />
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}));

export default MiniCartContent;
